import { CACHE_TIME } from '@/const/cache';
import { SignupState } from '@/types/Signup';

export type SetupIntentStatus = {
  setupIntentId: string;
  status: 'pending' | 'complete' | 'failed';
  customerId?: string;
  paymentMethod?: {
    brand?: string;
    last4?: string;
    expirationDate?: string;
    /** Set when the card came from a wallet — 'apple_pay', 'google_pay', … */
    wallet?: string;
    billingAddress?: {
      line1?: string | null;
      line2?: string | null;
      city?: string | null;
      state?: string | null;
      postal_code?: string | null;
    };
  };
};

/**
 * Reads back a confirmed SetupIntent.
 *
 * Submit asks the server rather than trusting `confirmSetup`'s return value:
 * the customer id and saved card are what the EPCC account is built from, and
 * Stripe is the authority on whether the setup actually succeeded.
 */
export async function readSetupIntent(
  setupIntentId: string
): Promise<SetupIntentStatus> {
  const response = await fetch(`/api/signup/setup-intent/${setupIntentId}`, {
    next: { revalidate: CACHE_TIME.NO_CACHE }
  });

  if (!response.ok) {
    throw new Error(`Failed to read setup intent ${setupIntentId}`);
  }

  return (await response.json()) as SetupIntentStatus;
}

/**
 * Runs the same card authorization the multi-page flow does, plus the prepaid
 * refusal. Throws a Stripe-caused error when the card is refused.
 */
export async function authorizeSetupIntent(
  setupIntentId: string,
  user: SignupState
): Promise<void> {
  const response = await fetch(
    `/api/signup/setup-intent/${setupIntentId}/authorize`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user })
    }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw Object.assign(
      new Error(body?.error ?? 'Card authorization failed', {
        cause: 'Stripe'
      }),
      { code: body?.code }
    );
  }
}
